var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { html } from 'lit';
import { property } from 'lit/decorators.js';
import { KEYS } from '../../util/event-keys';
import { BlocksElement } from '../BlocksElement';
import styles from './tab.styles';
/**
 * @slot default - The component content
 * @fires bl-tab-select - emitted when a tab is selected
 * @csspart base - The component's base wrapper.
 * @csspart button - The component's button wrapper.
 * @csspart wrap - The component's wrapper.
 * @csspart part-default - The component's default slot.
 */
export class BlocksTab extends BlocksElement {
    static { this.styles = styles; }
    constructor() {
        super();
        this.addEventListener('click', this.handleTabSelected);
        this.addEventListener('keydown', this.handleKeydown);
    }
    handleTabSelected() {
        if (this.disabled) {
            return;
        }
        this.dispatch({ eventName: 'bl-tab-select', detailObj: {} });
    }
    firstUpdated() {
        this.setAttribute('role', 'tab');
        this.setAttribute('tabindex', '-1');
        this.setAttribute('aria-selected', 'false');
    }
    updated(_changedProperties) {
        if (_changedProperties.has('ariaLabelledBy')) {
            this.setAttribute('id', this.ariaLabelledBy ?? '');
        }
        if (_changedProperties.has('ariaControls')) {
            this.setAttribute('aria-controls', this.ariaControls ?? '');
        }
        if (_changedProperties.has('isActive')) {
            this.setAttribute('tabindex', this.isActive ? '0' : '-1');
            this.setAttribute('aria-selected', `${this.isActive}`);
        }
        if (_changedProperties.has('disabled')) {
            if (this.disabled) {
                this.setAttribute('aria-disabled', 'true');
            }
            else {
                this.removeAttribute('aria-disabled');
            }
        }
    }
    handleKeydown(event) {
        if (event.key === KEYS.ENTER || event.key === KEYS.SPACE) {
            this.handleTabSelected();
        }
    }
    render() {
        const componentClassName = this.componentClassNames('cat-c-tab', {
            'cat-c-tab--large': this.size === 'lg' && this.variant !== 'contrast',
            'cat-c-tab--contrast': this.variant === 'contrast',
            'cat-c-tabs-span-container': this.variant === 'span-container',
            'cat-is-active': this.isActive,
            'cat-is-disabled': this.disabled,
            'cat-c-tab--responsive-full-width': this.behavior === 'responsive-full-width',
            'cat-c-tab--full-width': this.behavior === 'full-width'
        });
        return html `
      <div part="base" class="${componentClassName}">
        <div part="button" class="cat-c-tab__button">
          <div part="wrap" class="cat-c-tab__wrapper">
            <slot part="part-default"></slot>
          </div>
        </div>
      </div>
    `;
    }
}
__decorate([
    property({ type: Boolean })
], BlocksTab.prototype, "disabled", void 0);
__decorate([
    property({ type: Number })
], BlocksTab.prototype, "index", void 0);
__decorate([
    property({ type: String })
], BlocksTab.prototype, "ariaLabelledBy", void 0);
__decorate([
    property({ type: Boolean })
], BlocksTab.prototype, "isActive", void 0);
__decorate([
    property({ type: String })
], BlocksTab.prototype, "ariaControls", void 0);
__decorate([
    property({ type: String })
], BlocksTab.prototype, "variant", void 0);
__decorate([
    property({ type: String })
], BlocksTab.prototype, "behavior", void 0);
__decorate([
    property({ type: String })
], BlocksTab.prototype, "size", void 0);
if (customElements.get('cat-tab') === undefined) {
    customElements.define('cat-tab', BlocksTab);
}
